import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3 } from 'lucide-react';
import { useEmbedContext } from '@/hooks/useEmbedContext';
import { useQuestions, useFilterOptions } from '@/hooks/useQuestions';
import { useFilters } from '@/hooks/useFilters';
import { QuestionsTable } from '@/components/QuestionsTable';
import { FilterControls } from '@/components/FilterControls';
import { AdvancedFilterControls } from '@/components/AdvancedFilterControls';
import { Pagination } from '@/components/Pagination';
import { ExportDialog } from '@/components/ExportDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export function QuestionsPage() {
  const navigate = useNavigate();
  const { bridge } = useEmbedContext();
  const {
    filters,
    page,
    pageSize,
    sortBy,
    sortOrder,
    hasActiveFilters,
    setSearch,
    setStatus,
    setSource,
    setCategory,
    setAssignedDoctor,
    setPriority,
    setFavorite,
    setTags,
    setPage,
    setPageSize,
    setSort,
    resetFilters,
  } = useFilters();

  const { data: filterOptionsData } = useFilterOptions();
  const { data, isLoading, isError, error } = useQuestions(
    filters,
    page,
    pageSize,
    sortBy,
    sortOrder
  );

  // Set page title via bridge
  useEffect(() => {
    bridge?.setTitle('Questions');
  }, [bridge]);

  if (isError) {
    return (
      <div className="flex h-64 items-center justify-center text-destructive">
        Error: {error?.message || 'Failed to load questions'}
      </div>
    );
  }

  return (
    <div className="space-y-6 p-3 sm:p-6">
      <Card>
        <CardHeader className="flex flex-col gap-3 pb-4 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle>Questions</CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/questions/stats')}
            >
              <BarChart3 className="sm:mr-1 h-4 w-4" />
              <span className="hidden sm:inline">Statistics</span>
            </Button>
            <ExportDialog filters={filters} />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <FilterControls
            search={filters.search}
            status={filters.status}
            source={filters.source}
            onSearchChange={(value) => setSearch(value || undefined)}
            onStatusChange={setStatus}
            onSourceChange={setSource}
            onReset={resetFilters}
            hasActiveFilters={hasActiveFilters}
          />

          <AdvancedFilterControls
            category={filters.category}
            assignedDoctor={filters.assignedDoctor}
            priority={filters.priority}
            isFavorite={filters.isFavorite}
            tags={filters.tags}
            filterOptions={filterOptionsData}
            onCategoryChange={setCategory}
            onAssignedDoctorChange={setAssignedDoctor}
            onPriorityChange={setPriority}
            onFavoriteChange={setFavorite}
            onTagsChange={setTags}
          />

          <QuestionsTable
            data={data?.items || []}
            isLoading={isLoading}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={(column) => {
              if (column === sortBy) {
                setSort(column, sortOrder === 'desc' ? 'asc' : 'desc');
              } else {
                setSort(column, 'desc');
              }
            }}
          />

          {data && (
            <Pagination
              page={data.page}
              pageSize={data.pageSize}
              total={data.total}
              hasNext={data.hasNext}
              hasPrev={data.hasPrev}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
